import { PoweredByCuddles } from 'components/Cuddles/PoweredBy/PoweredByCuddles';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { useMarketplace } from 'hooks/useMarketplace';
import { useOnlineBookingState } from 'hooks/useOnlineBookingState';
import React from 'react';
import { useSelector } from 'react-redux';
import { dispatch } from 'store';
import {
    navigateToStep,
    updateOnlineBookingBookingItems
} from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import {
    DateTimePicker,
    DateTimePickerItem
} from 'components/OnlineBooking/DateTimePicker/DateTimePicker';
import { OnlineBookingFooter } from 'components/OnlineBooking/Footer/OnlineBookingFooter';
import { JoinWaitlistCta } from 'components/OnlineBooking/JoinWaitlistCta/JoinWaitlistCta';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import {
    getOnlineBookingGroomingAvailableStaffs,
    getOnlineBookingGroomingDate,
    getOnlineBookingGroomingItems,
    getOnlineBookingGroomingPreferredStaff,
    getOnlineBookingGroomingTime
} from 'reducers/onlineBooking/OnlineBookingSelectors';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import { OnlineBookingStep } from 'types/OnlineBooking';
import { getBusinessArea, trackDateTimeSelected } from 'utils/analytics';

export const OnlineBookingGroomingDatePicker: React.FC = () => {
    const marketplace = useMarketplace();
    const { isPending } = useOnlineBookingState();

    const selectedTime = useSelector(getOnlineBookingGroomingTime);
    const selectedDate = useSelector(getOnlineBookingGroomingDate);
    const preferredStaff = useSelector(getOnlineBookingGroomingPreferredStaff);
    const availableStaffs = useSelector(getOnlineBookingGroomingAvailableStaffs);
    const groomingBookingItems = useSelector(getOnlineBookingGroomingItems);

    const isValid = React.useMemo(() => {
        return selectedDate !== undefined && selectedTime !== undefined;
    }, [selectedDate, selectedTime]);

    const backHandler = React.useCallback(() => {
        const previousStep = marketplace.onlineBookingPreferences.allowSelectStaff
            ? OnlineBookingStep.StaffList
            : OnlineBookingStep.ServiceList;
        dispatch(navigateToStep(previousStep));
    }, [marketplace.onlineBookingPreferences.allowSelectStaff]);

    const dateTimePickerChangeHandler = React.useCallback(
        (updatedItems: Array<DateTimePickerItem>) => {
            // Convert back to grooming booking items format
            const updatedGroomingItems = groomingBookingItems.map((originalItem, index) => {
                const updatedItem = updatedItems[index];
                if (!updatedItem) return originalItem;

                let staff = preferredStaff;

                if (!staff && updatedItem.time?.staffId) {
                    staff = availableStaffs.find(s => s.id === updatedItem.time?.staffId);
                }

                return {
                    ...originalItem,
                    date: updatedItem.date,
                    dateSelectedByClient: updatedItem.dateSelectedByClient,
                    time: updatedItem.time,
                    staff
                };
            });

            dispatch(updateOnlineBookingBookingItems(updatedGroomingItems));
        },
        [groomingBookingItems, preferredStaff, availableStaffs]
    );

    const submitHandler = React.useCallback(() => {
        // Analytics
        if (selectedDate && selectedTime) {
            trackDateTimeSelected({
                marketplace_slug: marketplace.slug,
                business_area: getBusinessArea(groomingBookingItems),
                booking_date: selectedDate.format('YYYY-MM-DD'),
                time_of_day: selectedTime.hour < 12 ? 'morning' : 'afternoon'
            });
        }

        dispatch(navigateToStep(OnlineBookingStep.ServiceCategory));
    }, [selectedDate, selectedTime, marketplace.slug, groomingBookingItems]);

    return (
        <TwoColumnsLayout header={<OnlineBookingHeader onBack={backHandler} />}>
            <LeftColumn contentClassName="flex flex-col flex-1">
                <h1 className="h2 underlined w-full mb-5">Select date & time</h1>

                <DateTimePicker
                    preferredStaff={preferredStaff}
                    items={groomingBookingItems.map(item => ({
                        pet: item.pet,
                        service: item.service,
                        date: item.date,
                        dateSelectedByClient: item.dateSelectedByClient,
                        time: item.time
                    }))}
                    enableNextAvailableDate
                    onChange={dateTimePickerChangeHandler}
                />

                <JoinWaitlistCta category={MarketplaceServiceCategory.GROOMING} />

                <div className="mt-auto hide-on-mobile-and-tablet pt-6">
                    <PoweredByCuddles />
                </div>
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={
                    <OnlineBookingFooter
                        disabled={isPending || !isValid}
                        onSubmit={submitHandler}
                    />
                }
            >
                <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                    <MarketplaceInfo inverted marketplace={marketplace} />

                    <OnlineBookingResume />
                </div>
            </RightColumn>
        </TwoColumnsLayout>
    );
};
